import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import Can from 'components/Can';
import { useQuery } from 'hooks';
import { useConfirm } from 'hooks';
import { debounce } from 'lodash';
import Layout from 'components/Layout';
import { Table } from 'components/UI';
import useStyles from './location.styles';
import MapComponent from 'components/Map/Map';
import Hidden from '@material-ui/core/Hidden';
import { useHistory } from 'react-router-dom';
import EditIcon from '@material-ui/icons/Edit';
import { buildUrl, toQueryString } from 'utils';
import PolygonItem from 'components/Map/Polygon';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import { SearchField } from 'components/FormFields';
import { truncateKeywordSpaces } from 'utils/utils';
import IconButton from '@material-ui/core/IconButton';
import { locationActions } from 'state/modules/locations';
import MarkerWithLabel from 'components/Map/MarkerWithLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Box, Paper, Button, Container, Typography, Grid } from '@material-ui/core';

const DEFAULT_COORDS = {
  lat: 41.2145219,
  lng: -109.4779065,
};

const DEFAULT_MAPS_OPTIONS = {
  scrollwheel: false,
};

const LocationsList = () => {
  const classes = useStyles();
  const urlParams = useQuery();
  const history = useHistory();
  const dispatch = useDispatch();
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const loading = useSelector((state) => state.locations?.loading);
  const locations = useSelector((state) => state.locations?.locations);
  const pagination = useSelector((state) => state.locations?.pagination);
  const fetchingDeleteLocation = useSelector((state) => state.locations?.fetchingDeleteLocation);
  const [params, setParams] = useState({ page: 1, limit: 10, ...urlParams });
  const [mapRef, setMapRef] = useState({});
  const [markers, setMarkers] = useState([]);
  const [mapZoom, setMapZoom] = useState(8);
  const [locationSelected, setLocationSelected] = useState();
  const [resetCurrentPage, setResetCurrentPage] = useState(false);

  const [toggleDeleteLocation, DeleteLocationConfirm] = useConfirm({
    title: i18n.t('locations.delete_message'),
    message: i18n.t('locations.delete_message_description', {
      name: locationSelected?.name,
    }),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: () => {
      dispatch(locationActions.deleteLocation(locationSelected?.id));
    },
  });

  const columns = [
    {
      name: 'Zone Name',
      selector: 'name',
      format: (row) => row?.name || '--',
      sortable: true,
    },
    {
      name: 'Address',
      selector: 'address',
      format: (row) => row?.address || '--',
      sortable: true,
    },
    {
      name: 'Time Zone',
      selector: 'time_zone',
      format: (row) => row?.time_zone || '--',
      sortable: true,
    },
    {
      name: 'Actions',
      sortable: false,
      cell: (row) => {
        return (
          <Box display="flex" alignItems="center" justifyContent="center">
            <IconButton
              onClick={() => history.push(buildUrl(`/locations/${row.id}/update`))}
              aria-label="edit"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              disabled={fetchingDeleteLocation}
              data-cy="delete-location"
              onClick={() => {
                setLocationSelected(row);
                toggleDeleteLocation();
              }}
              aria-label="delete"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
      button: true,
    },
  ];

  const fetchLocations = useCallback(
    (params = {}) => {
      dispatch(locationActions.resetLocationState());
      if (params?.keyword) {
        params.keyword = truncateKeywordSpaces(params.keyword);
      }
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        locationActions.fetchLocations(params, { signal: abortController?.current?.signal })
      );
    },
    [dispatch]
  );

  const fetchLocationsDebounced = useCallback(
    debounce((params) => fetchLocations(params), 500),
    [fetchLocations]
  );

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchLocationsDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchLocationsDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchLocations(params);
  }, []);

  const handleParams = (params) => {
    const data = { ...params };
    setParams(data);
    const _params = toQueryString(data);
    history.replace(buildUrl(`/locations${_params}`));
  };

  /**
   * Handle page change on table
   * @param {number} page Number of the page
   */
  const handlePageChange = (page) => {
    const newParams = { ...params, page };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle sort change on table
   * @param {string} column Column of the table
   * @param {string} sortDirection Sort direction
   */
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  /**
   * Handle per rows (limit) change on table
   * @param {number} limit New limit value
   */
  const handlePerRowsChange = async (limit) => {
    const newParams = { ...params, page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Set map bounds and create markers data
   * @param {*} locations
   */
  const setMapBoundsAndMarkers = useCallback(
    (locations) => {
      const markers = locations
        .map((_location) => {
          const coords = _location?.center || null;
          if (!coords) {
            return null;
          }

          return {
            position: coords,
            name: _location?.name,
          };
        })
        .filter((location) => location);

      setMarkers(markers);

      // Set bounds and center map to desired location
      if (window.google) {
        const bounds = getBounds(markers).then((bounds) => {
          // bounds.extend(coords);
          if (bounds && mapRef && mapRef.fitBounds) {
            mapRef.fitBounds(bounds);
          }
        });
      }
    },
    [mapRef]
  );

  useEffect(() => {
    if (locations.length) {
      setMapBoundsAndMarkers(locations);
    }
  }, [locations, setMapBoundsAndMarkers]);

  /**
   * Get new bounds to be used to center the map
   * @param {*} zones
   */
  const getBounds = async (locations) => {
    const core = await window.google.maps.importLibrary('core');
    const bounds = new core.LatLngBounds();
    locations.forEach((_location) => {
      const { lat, lng } = _location?.position;
      bounds.extend({ lat, lng });
    });
    return bounds;
  };

  const onZoomChange = () => {
    let zoom = 8;
    if (mapRef?.getZoom) {
      zoom = mapRef?.getZoom();
    }
    setMapZoom(zoom);
  };

  const handleRowClick = (row) => {
    history.push(buildUrl(`/locations/${row?.id}`));
  };

  // search by keyword
  const search = (name, value) => {
    const filters = {
      page: 1,
      limit: params?.limit || 10,
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach(
      (key) => (!filters[key] || filters[key] === '') && delete filters[key]
    );
    handleParams(filters);
    setResetCurrentPage((prevState) => !prevState);
  };

  return (
    <Layout title="Locations">
      <DeleteLocationConfirm />
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container direction="row" alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h6" gutterBottom>
                  <Box fontWeight="fontWeightBold" fontSize={24} data-cy="title" mb={1}>
                    Locations
                  </Box>
                </Typography>
              </Grid>
              <Can I="create" on="location">
                <Grid item>
                  <Hidden only="xs">
                    <Button
                      onClick={() => history.push(buildUrl('/locations/create'))}
                      variant="contained"
                      data-cy="create-btn"
                      style={{ textTransform: 'capitalize' }}
                      color="primary"
                    >
                      {i18n.t('locations.create')}
                    </Button>
                  </Hidden>
                </Grid>
              </Can>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ paddingBottom: '25px' }}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-end"
              spacing={2}
            >
              <Grid item xs={12} sm={6}>
                <SearchField
                  data-cy="search-location-input"
                  label={'Search location'}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.searchIcon,
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  className={classes.searchBox}
                  field={{
                    onChange: search,
                    name: 'keyword',
                    value: params.keyword,
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={7}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={locations}
                columns={columns}
                loading={loading}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                defaultSortAsc={!params?.sort_desc}
                defaultSortField={params?.sort_field}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                paginationResetDefaultPage={resetCurrentPage}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} sm={12} md={5}>
            <Paper elevation={2} className={classes.paper}>
              <Box className={classes.mapContainer}>
                <MapComponent
                  zoom={4}
                  center={DEFAULT_COORDS}
                  defaultOptions={DEFAULT_MAPS_OPTIONS}
                  onZoomChanged={onZoomChange}
                  setMapRef={(ref) => setMapRef(ref)}
                >
                  {locations &&
                    mapZoom > 11 &&
                    locations.map((zone, index) => (
                      <PolygonItem
                        key={zone.id}
                        path={zone?.zone?.slice(0, -1)}
                        options={{
                          fillColor: zone?.market_id ? '#007bff' : '#f69041',
                          strokeWeight: 1,
                          strokeColor: zone?.market_id ? '#007bff' : '#f69041',
                          fillOpacity: zone.market_id ? 0.2 : 0.5,
                        }}
                      />
                    ))}

                  {markers &&
                    mapZoom <= 11 &&
                    markers.map((marker, index) => (
                      <MarkerWithLabel
                        key={index}
                        position={marker?.position}
                        label={marker?.name}
                        labelStyle={{
                          textAlign: 'center',
                          backgroundColor: 'rgba(255,255,255,0.8)',
                          fontSize: 16,
                          padding: 3,
                        }}
                      />
                    ))}
                </MapComponent>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default LocationsList;
